import { ProductJsonLd, CollectionPageJsonLd } from 'next-seo'
import PageSEO from '@/components/SEO/PageSEO'
import classes from './Collection.module.scss'
import { getCollectionContentByHandle } from '@/utils/getCollectionContentByHandle'
import { getSanityClient } from '@/services/sanityClient'
import { PreviewButton } from '@/components/Preview/PreviewButton'
import ContentSections from '@/components/Sections/ContentSections'
import getCollectionListProducts from '@/utils/getCollectionListProducts'
import getCollectionHeroProducts from '@/utils/getCollectionHeroProducts'
import { getAllCollections } from '@/utils/getAllCollections'

function Collection({ page, products = [], preview, previewToken }) {
  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: page?.fields?.seo?.metaTitle || '',
    description: page?.fields?.seo?.metaDesc || '',
    url: `${process.env.NEXT_PUBLIC_DOMAIN}/collections/${page?.content?.handle}`,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: Array.isArray(products) ? products.filter(product => product?.content).map((product, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Product",
          name: product.content?.title || '',
          description: product.description || '',
          image: product.content?.media?.[0]?.src || '',
          url: `${process.env.NEXT_PUBLIC_DOMAIN}/products/${product.content?.handle || ''}`,
          offers: {
            "@type": "Offer",
            price: product.variants?.[0]?.price || '0',
            priceCurrency: "USD",
            availability: product.variants?.[0]?.availableForSale
              ? "https://schema.org/InStock"
              : "https://schema.org/OutOfStock"
          }
        }
      })) : []
    }
  };

  return (
    <div className={classes['collection']}>
      {preview && previewToken && <PreviewButton />}
      <PageSEO
        seo={page?.fields?.seo}
        collection={page}
        images={page?.content?.image ? [{
          url: page?.content?.image?.src,
          alt: page?.content?.title
        }] : []}
      />
      {page && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(collectionSchema)
          }}
        />
      )}
      {Array.isArray(products) && products.filter(product => product?.content).map((product, index) => (
        <ProductJsonLd
          key={product.content?.handle}
          productName={product.content?.title || ''}
          description={product.description || ''}
          images={[product.content?.media?.[0]?.src].filter(Boolean)}
          offers={{
            price: product.variants?.[0]?.price || '0',
            priceCurrency: 'USD',
            availability: product.variants?.[0]?.availableForSale
              ? 'https://schema.org/InStock'
              : 'https://schema.org/OutOfStock'
          }}
        />
      ))}
      <ContentSections
        sections={page?.fields?.content}
        product={products}
        isCollectionPage={true}
      />
    </div>
  )
}

export default Collection

export async function getStaticPaths() {
  const collections = await getAllCollections()
  const paths = collections
    .filter(
      (collection) =>
        collection.content?.handle && collection.content?.handle !== 'swapables',
    )
    .map((collection) => ({ params: { handle: collection.content?.handle } }))
  return {
    paths,
    fallback: 'blocking',
  }
}

export async function getStaticProps({ params, draftMode }) {
  if (params.handle === 'swapables') {
    return {
      notFound: true,
    }
  }

  const preview = draftMode || false
  const previewToken = preview ? process.env.SANITY_READ_TOKEN : ``
  const products = []
  if (preview && !previewToken) {
    throw new Error(
      `Preview mode is active, but SANITY_READ_TOKEN is not set in environment variables`,
    )
  }

  const sanityClient = getSanityClient(previewToken)

  const content = await getCollectionContentByHandle(
    params.handle,
    sanityClient,
  )

  if (!content) {
    return {
      notFound: true,
    }
  }

  if (
    content?.fields?.content?.some(
      (content) => content._type === 'collectionList',
    )
  ) {
    products.push(...(await getCollectionListProducts(content)))
  }

  if (
    content?.fields?.content?.some(
      (content) => content._type === 'collectionHero',
    )
  ) {
    products.push(...(await getCollectionHeroProducts(content)))
  }

  return {
    props: {
      page: content,
      products: products.map((product) => ({
        ...product,
        description:
          product.metafields?.find((meta) => meta.key === 'short_description')
            ?.value || null,
      })),
      preview,
      previewToken,
    },
  }
}
